import type { PersistenceAwareStorage } from '../ports/storage.ts';

/** The subset of DOM `Storage` we use (no DOM lib in this package). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface WebStorageOptions {
  /**
   * Storage to wrap. Omit to detect `globalThis.localStorage`; pass `null`
   * to force the in-memory fallback.
   */
  storage?: StorageLike | null;
}

const PROBE_KEY = '__dcs_probe__';

function detectLocalStorage(): StorageLike | null {
  try {
    // Reading the property itself can throw (SecurityError when blocked).
    const candidate = (globalThis as { localStorage?: StorageLike }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

function isWritable(storage: StorageLike): boolean {
  try {
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Storage over `localStorage` when it is available and writable, otherwise
 * an in-memory map. Never throws. A write that fails (quota) degrades that
 * key to memory for the rest of the session; `persistent` then reads `false`.
 */
export function createWebStorage(options: WebStorageOptions = {}): PersistenceAwareStorage {
  const candidate = options.storage === undefined ? detectLocalStorage() : options.storage;
  const backing = candidate !== null && isWritable(candidate) ? candidate : null;

  const memory = new Map<string, string>();
  // Keys whose latest value lives only in memory (failed write).
  const degraded = new Set<string>();

  return {
    get persistent() {
      return backing !== null && degraded.size === 0;
    },
    get(key) {
      if (backing === null || degraded.has(key)) return memory.get(key) ?? null;
      try {
        return backing.getItem(key);
      } catch {
        return memory.get(key) ?? null;
      }
    },
    set(key, value) {
      memory.set(key, value);
      if (backing === null) return;
      try {
        backing.setItem(key, value);
        degraded.delete(key);
      } catch {
        degraded.add(key);
      }
    },
    remove(key) {
      memory.delete(key);
      degraded.delete(key);
      if (backing === null) return;
      try {
        backing.removeItem(key);
      } catch {
        // Nothing durable to clean up; the memory copy is already gone.
      }
    },
  };
}
